import { Hand } from "lucide-react";
import { useEffect, useRef } from "react";
import useSound from "use-sound";
import { useShallow } from "zustand/react/shallow";

import notificationSound from "@/assets/sounds/notification01.wav";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

export function RaisedHandsCounter() {
  const [play] = useSound(notificationSound, { volume: 0.3 });
  const lastPlayedTimeRef = useRef<number>(0);
  const DEBOUNCE_DELAY = 1000; // 1 second delay between sounds
  const previousCountRef = useRef<number>(0);

  const participantsWithHandRaised = useVidyoStore(
    useShallow((state) =>
      state.participants.participants.filter((p) => p.isHandRaised),
    ),
  );
  const isRaisedHandSoundEnabled = useVidyoStore(
    (state) => state.participants.isRaisedHandSoundEnabled,
  );

  const openParticipantsDrawer = useVidyoStore(
    (state) => state.participantsDrawer.open,
  );

  const nbOfParticipantsWithHandRaised = participantsWithHandRaised.length;

  useEffect(() => {
    const hasIncreased =
      nbOfParticipantsWithHandRaised > previousCountRef.current;
    if (hasIncreased && isRaisedHandSoundEnabled) {
      const now = Date.now();
      if (now - lastPlayedTimeRef.current >= DEBOUNCE_DELAY) {
        play();
        lastPlayedTimeRef.current = now;
      }
    }
    previousCountRef.current = nbOfParticipantsWithHandRaised;
  }, [nbOfParticipantsWithHandRaised, play, isRaisedHandSoundEnabled]);

  if (nbOfParticipantsWithHandRaised === 0) {
    return null;
  }

  const handleButtonClick = () => {
    openParticipantsDrawer();
  };

  return (
    <button
      className="focus-visible:ring-offset-20 inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-0.5 focus-visible:outline-none focus-visible:ring focus-visible:ring-ring"
      onClick={handleButtonClick}
    >
      <Hand
        key={participantsWithHandRaised.length}
        className="size-3 animate-shake text-primary"
      />
      <p className="text-sm font-medium text-secondary">
        {nbOfParticipantsWithHandRaised}
      </p>
    </button>
  );
}
